import type { TableSorting } from "@aurem/ui";
import { useCallback, useMemo } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UseTableSortingProps<TFilters extends Record<string, any>> = {
  filters: TFilters;
  setFilters: (filters: Partial<TFilters>) => void;
  removeFilters: (keys: (keyof TFilters)[]) => void;
  sortFieldKey: keyof TFilters;
  sortOrderKey: keyof TFilters;
  pageKey?: keyof TFilters;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useTableSorting = <TFilters extends Record<string, any>>({
  filters,
  setFilters,
  removeFilters,
  sortFieldKey,
  sortOrderKey,
  pageKey,
}: UseTableSortingProps<TFilters>): TableSorting => {
  const onSort = useCallback(
    (key: string, direction: "asc" | "desc" | null) => {
      if (direction === null) {
        if (pageKey) {
          setFilters({
            [sortFieldKey]: undefined,
            [sortOrderKey]: undefined,
            [pageKey]: "1",
          } as Partial<TFilters>);
        } else {
          removeFilters([sortFieldKey, sortOrderKey]);
        }
        return;
      }

      setFilters({
        [sortFieldKey]: key,
        [sortOrderKey]: direction,
        ...(pageKey ? { [pageKey]: "1" } : {}),
      } as Partial<TFilters>);
    },
    [setFilters, removeFilters, sortFieldKey, sortOrderKey, pageKey],
  );

  return useMemo((): TableSorting => {
    const field = filters[sortFieldKey];
    const order = filters[sortOrderKey];

    return {
      key: field || null,
      direction: (order as "asc" | "desc") || null,
      onSort,
      server: true,
    };
  }, [filters, sortFieldKey, sortOrderKey, onSort]);
};
